/**
 * Zustand store for admin language testing
 *
 * Allows system administrators to quickly test the application in different languages
 * via a floating action button. The testing mode persists across page reloads via localStorage.
 *
 * This implements a two-tier language system:
 * - Testing override (localStorage): Temporary override for admin testing
 * - User preference (database): Normal language preference for all users
 *
 * When testing is active, the override takes priority. When exiting, reverts to user's database preference.
 *
 * @example
 * const { isActive, selectedLanguage, startLanguageTesting, stopLanguageTesting, setLanguage } = useLanguageTesting();
 *
 * // Start language testing mode
 * startLanguageTesting();
 *
 * // Change language (override)
 * setLanguage('es');
 *
 * // Exit language testing mode (reverts to database preference)
 * stopLanguageTesting();
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { SupportedLanguage } from "../i18n/config";

interface LanguageTestingState {
  /** Whether language testing mode is currently active */
  isActive: boolean;

  /** Testing override language (null = no override, use database preference) */
  selectedLanguage: SupportedLanguage | null;

  /** Start language testing mode (shows FAB) */
  startLanguageTesting: () => void;

  /** Stop language testing mode (hides FAB, clears override) */
  stopLanguageTesting: () => void;

  /** Set language override (persists to localStorage) */
  setLanguage: (lang: SupportedLanguage) => void;
}

export const useLanguageTesting = create(
  persist<LanguageTestingState>(
    (set) => ({
      isActive: false,
      selectedLanguage: null,

      startLanguageTesting: () => {
        set({ isActive: true });
      },

      stopLanguageTesting: () => {
        set({
          isActive: false,
          selectedLanguage: null, // Clear override when exiting
        });
      },

      setLanguage: (lang) => {
        set({ selectedLanguage: lang });
      },
    }),
    {
      name: "language-testing-storage",
    }
  )
);
